'use client';

// Import renderer, note that introducing the CanvasRenderer or SVGRenderer is a required step
import { t } from '@lingui/macro';
import { useLingui } from '@lingui/react';
import { Box } from '@mui/material';
import type { UserQuarkHistoricRowT } from '@prismo-io/schemas';
// import the core library.
import ReactEChartsCore from 'echarts-for-react/lib/core';
// Import charts, all with Chart suffix
import { BarChart, LineChart } from 'echarts/charts';
// import components, all suffixed with Component
import { GridComponent, TooltipComponent } from 'echarts/components';
// Import the echarts core module, which provides the necessary interfaces for using echarts.
import * as echarts from 'echarts/core';
import { SVGRenderer } from 'echarts/renderers';
import { type ComponentProps, type FC, useCallback, useMemo } from 'react';

type DataType = 'skills' | 'appetences' | 'formations' | 'experiences';

type Props = {
  data: UserQuarkHistoricRowT;
  types?: DataType[];
  width?: string;
  height?: string;
  isBlury?: boolean;
};

// Register the required components
echarts.use([
  TooltipComponent,
  GridComponent,
  BarChart,
  LineChart,
  SVGRenderer,
]);

export const QuarkHistoryLinesBarsGraph: FC<Props> = (props: Props) => {
  const {
    data,
    types = ['skills'],
    isBlury = false,
    width = '800px',
    height = '540px',
  } = props;

  const withSkills = useMemo(() => types.includes('skills'), [types]);
  const withAppetences = useMemo(() => types.includes('appetences'), [types]);
  const withFormations = useMemo(() => types.includes('formations'), [types]);
  const withExperiences = useMemo(() => types.includes('experiences'), [types]);

  const nbItemsToRemove = useMemo(() => {
    const firstSkills = data.skills.count.findIndex((value) => value !== 0);
    const firstAppetences = data.skills.count.findIndex((value) => value !== 0);
    const firstExperiences = data.experiences.count.findIndex(
      (value) => value !== 0
    );
    const firstFormations = data.formations.count.findIndex(
      (value) => value !== 0
    );

    const ordered = [
      withSkills ? firstSkills : -1,
      withAppetences ? firstAppetences : -1,
      withExperiences ? firstExperiences : -1,
      withFormations ? firstFormations : -1,
    ]
      .filter((v) => v >= 0)
      .sort((a, b) => a - b);

    return ordered[0] ?? 0;
  }, [data, withSkills, withAppetences, withExperiences, withFormations]);

  const blurFilter = isBlury ? 'blur(4px)' : 'blur(0)';

  const { i18n } = useLingui();

  const formattedDate = useCallback(
    (date: Date) =>
      date
        ? new Date(date).toLocaleDateString(i18n.locale as Intl.LocalesArgument)
        : null,
    [i18n]
  );

  const options = useMemo(
    (): ComponentProps<typeof ReactEChartsCore>['option'] => ({
      tooltip: {
        trigger: 'axis',
        axisPointer: {
          type: 'cross',
          crossStyle: {
            color: '#999',
          },
        },
      },
      toolbox: {
        feature: {
          dataView: { show: true, readOnly: false },
          magicType: { show: true, type: ['line', 'bar'] },
          restore: { show: true },
          saveAsImage: { show: true },
        },
      },
      legend: {
        data: [
          ...(withSkills ? [t`Compétences`] : []),
          ...(withAppetences ? [t`Appétences`] : []),
          ...(withFormations ? [t`Formations`] : []),
          ...(withExperiences ? [t`Expériences`] : []),
          ...(withSkills ? [t`Compétences identifiées au total`] : []),
          ...(withAppetences ? [t`Appétences identifiées au total`] : []),
          ...(withFormations ? [t`Formations identifiées au total`] : []),
          ...(withExperiences ? [t`Expériences identifiées au total`] : []),
        ],
      },
      xAxis: [
        {
          type: 'category',
          data: data.dates
            .map((date) => formattedDate(date))
            .slice(nbItemsToRemove),
          axisPointer: {
            type: 'shadow',
          },
        },
      ],
      yAxis: [
        ...(withSkills
          ? [
              {
                type: 'value',
                name: t`Compétences`,
                min: 0,
              },
            ]
          : []),
        ...(withAppetences
          ? [
              {
                type: 'value',
                name: t`Appétences`,
                min: 0,
              },
            ]
          : []),
        ...(withFormations
          ? [
              {
                type: 'value',
                name: t`Formations`,
                min: 0,
              },
            ]
          : []),
        ...(withExperiences
          ? [
              {
                type: 'value',
                name: t`Expériences`,
                min: 0,
              },
            ]
          : []),
      ],
      series: [
        ...(withSkills
          ? [
              {
                name: t`Compétences`,
                type: 'bar',
                tooltip: {
                  valueFormatter: (value: number) => t`${value} identifiées`,
                },
                data: data.skills.count.slice(nbItemsToRemove),
              },
            ]
          : []),
        ...(withAppetences
          ? [
              {
                name: t`Appétences`,
                type: 'bar',
                tooltip: {
                  valueFormatter: (value: number) => t`${value} identifiées`,
                },
                data: data.skills.count.slice(nbItemsToRemove),
              },
            ]
          : []),
        ...(withFormations
          ? [
              {
                name: t`Formations`,
                type: 'bar',
                tooltip: {
                  valueFormatter: (value: number) => t`${value} identifiées`,
                },
                data: data.formations.count.slice(nbItemsToRemove),
              },
            ]
          : []),
        ...(withExperiences
          ? [
              {
                name: t`Expériences`,
                type: 'bar',
                tooltip: {
                  valueFormatter: (value: number) => t`${value} identifiées`,
                },
                data: data.experiences.count.slice(nbItemsToRemove),
              },
            ]
          : []),

        ...(withSkills
          ? [
              {
                name: t`Compétences identifiées au total`,
                type: 'line',
                tooltip: {
                  valueFormatter: (value: number) => t`${value} identifiées`,
                },
                data: data.skills.total.slice(nbItemsToRemove),
              },
            ]
          : []),
        ...(withAppetences
          ? [
              {
                name: t`Appétences identifiées au total`,
                type: 'line',
                tooltip: {
                  valueFormatter: (value: number) => t`${value} identifiées`,
                },
                data: data.skills.total.slice(nbItemsToRemove),
              },
            ]
          : []),
        ...(withFormations
          ? [
              {
                name: t`Formations identifiées au total`,
                type: 'line',
                tooltip: {
                  valueFormatter: (value: number) => t`${value} identifiées`,
                },
                data: data.formations.total.slice(nbItemsToRemove),
              },
            ]
          : []),
        ...(withExperiences
          ? [
              {
                name: t`Expériences identifiées au total`,
                type: 'line',
                tooltip: {
                  valueFormatter: (value: number) => t`${value} identifiées`,
                },
                data: data.experiences.total.slice(nbItemsToRemove),
              },
            ]
          : []),
      ],
    }),
    [
      i18n,
      isBlury,
      withSkills,
      withAppetences,
      withFormations,
      withExperiences,
      formattedDate,
      nbItemsToRemove,
    ]
  );

  if (!data) {
    return null;
  }

  return (
    <Box
      component="div"
      position="relative"
      width={width}
      height={height}
      sx={{ filter: blurFilter }}
    >
      <ReactEChartsCore
        echarts={echarts}
        option={options}
        notMerge={true}
        lazyUpdate={true}
        theme={'theme_name'}
        style={{ height: '100%', width: '100%', position: 'relative' }}
        opts={{}}
      />
    </Box>
  );
};
